import { ApiResponse, AuthResponse, User, ApiBypass, ApiResponseData } from '../types/api';

import * as Auth from '../services/auth';
import * as Http from '../services/http';
import * as Bypass from '../services/bypass';
import * as Logger from '../services/logger';
import { ensureHostInfo } from '../services/host';
import { isBrandSuspendedError, type SessionRefusalReason } from '../services/session-refusal';
import {
    KEEPALIVE_KEY,
    KEEPALIVE_RETRY_MS,
    keepAliveDelay,
    keepAliveDue,
    stampKeepAlive
} from '../services/session-keepalive';
import { hasTouchScreen, isMobile } from './Utils';
import { buildUserFromAuthResponse } from './auth-user';
import { Media } from '../types/media';

interface HostInfo {
    brandId?: number;
    brandName?: string;
    timezoneOffset?: number;
    logo?: Media;
    favIcon?: Media;
    bodyBackground?: Media;
    menuBackground?: Media;
    bodyBackgroundDark?: Media;
    menuBackgroundDark?: Media;
    theme?: any;
    culture?: string;
    currency?: string;
    userFlowKey?: string;
    isDev?: boolean;
    apiHost?: string;
    devServer?: string;
    hdPhotoHubDomain?: string;
}

/** Shared in-flight authorize so concurrent callers (session restore + Api.fetch) share one request. */
let authorizePromise: Promise<string | undefined> | null = null;

/**
 * Per-call extras for `Api.fetch` and the verbs built on it (ROBO-616).
 *
 * `headers` are sent IN ADDITION to the ones `Api` sets itself, never instead of them: the bearer
 * token `Api` attaches wins over an `Authorization` passed here, so a caller cannot swap the
 * session's credential for another. They exist for the request-scoped proofs the API reads from a
 * header — `hd-download-guid` for a guest holding a site's link, `hd-brand-host` for
 * `Calendar/availability` — which an anonymous call pairs with `skipAuth`, so no session is
 * looked for at all.
 */
export type ApiRequestOptions = Pick<RequestInit, 'keepalive' | 'headers'>;

/** `RequestInit['headers']` in any of its three shapes, as the plain record `Http.request` spreads. */
function headersToRecord(headers: HeadersInit | undefined): Record<string, string> {
    if (!headers) return {};
    if (headers instanceof Headers) return Object.fromEntries(headers.entries());
    if (Array.isArray(headers)) return Object.fromEntries(headers);
    return headers;
}

export interface ApiInterface {
    apiBypass: ApiBypass[];
    apiHost: string;
    apiLog?: Logger.LogEntry[];
    apiNotify?: (list: Logger.LogEntry[]) => void;
    accessToken?: string;
    bypassSet?: ((bypasses: ApiBypass[], length?: number) => void) | null;
    databaseLog?: Logger.DatabaseLogEntry[];
    databaseNotify?: (list: Logger.DatabaseLogEntry[]) => void;
    hostInfo?: HostInfo;
    persistTmr?: NodeJS.Timeout;
    refreshToken?: string;
    refreshTimer?: NodeJS.Timeout;
    requestLog?: Logger.RequestLogEntry[];
    requestNotify?: (list: Logger.RequestLogEntry[]) => void;
    sessionKey?: string;
    user?: User;
    validTo?: Date;
    webhookLog?: Logger.WebhookLogEntry[];
    webhookNotify?: (list: Logger.WebhookLogEntry[]) => void;
    /**
     * Called by SessionProvider; invoked whenever session state is updated. `persist` says
     * whether the durable localStorage/cookie session key should be written — see `login`'s
     * `rememberMe`.
     */
    onSessionUpdate?: (data: AuthResponse, persist?: boolean) => void;
    /**
     * ROBO-478. Set by SessionProvider, like `onSessionUpdate`; invoked once when the server refuses
     * to start (login, authorize) or continue (refresh) this session — today only a 403
     * `BRAND_SUSPENDED`. By the time it fires Api has already ended the session: tokens, user,
     * timers and the persisted session key are cleared, and no retry is scheduled.
     */
    onSessionRefused?: (reason: SessionRefusalReason) => void;
    authorize(sessionKey?: string): Promise<string | undefined>;
    bypassFind(verb: RequestInit['method'], url: string): ApiResponse | undefined;
    bypassLoad(): void;
    bypassSave(bypasses: ApiBypass[]): void;
    delete(url: string, skipAuth?: Boolean): Promise<ApiResponse>;
    ensureFreshToken(): Promise<string | undefined>;
    fetch<T, U = unknown>(
        method: RequestInit['method'],
        url: string,
        data?: T,
        skipAuth?: Boolean,
        opts?: ApiRequestOptions
    ): Promise<ApiResponse<U>>;
    get<T, U>(
        url: string,
        data?: T,
        skipAuth?: Boolean,
        opts?: ApiRequestOptions
    ): Promise<ApiResponse<U>>;
    hasTouchScreen(): boolean;
    isMobile(): boolean;
    login(email: string, password: string, rememberMe?: boolean): Promise<string | undefined>;
    logApiRequest<T>(method: RequestInit['method'], url: string, payload: T): Logger.LogEntry;
    patch<T, U>(
        url: string,
        data?: T,
        skipAuth?: Boolean,
        opts?: ApiRequestOptions
    ): Promise<ApiResponse<U>>;
    post<T, U>(
        url: string,
        data?: T,
        skipAuth?: Boolean,
        opts?: ApiRequestOptions
    ): Promise<ApiResponse<U>>;
    put<T, U>(url: string, data?: T, skipAuth?: Boolean): Promise<ApiResponse<U>>;
    persistSession(): void;
    refresh(): Promise<string | Boolean | undefined>;
    response<U>(response: ApiResponse<U>, event: Logger.LogEntry): Promise<ApiResponse<U>>;
    /** `persist` (default true) controls the durable localStorage/cookie write — see `login`'s `rememberMe`. */
    sessionUpdate(data?: AuthResponse, persist?: boolean): void;
}

const REFRESH_RETRY_BASE_MS = 1_000;
const REFRESH_RETRY_MAX_MS = 30_000;

/**
 * Refresh this far ahead of expiry rather than waiting for a 401. Covers the round trip
 * plus any clock skew between this browser and whoever validates the token — the
 * ai-router worker checks exp against Cloudflare edge time, not ours.
 */
const TOKEN_STALE_THRESHOLD_MS = 30_000;

/** In-flight refresh, shared by every caller so concurrent triggers collapse into one call. */
let refreshInFlight: Promise<boolean> | null = null;
let refreshRetryTimer: NodeJS.Timeout | undefined;
let refreshFailures = 0;

/**
 * A refresh the server actively rejected — the refresh token is expired, revoked, or
 * invalid, so the session is genuinely over. Anything else (offline, DNS failure, 5xx,
 * timeout) never reached a verdict and is treated as transient. A request that never
 * reached the server rejects with a TypeError, which has no status and so lands here
 * as transient.
 */
function isTerminalRefreshError(error: unknown): boolean {
    return error instanceof Http.HttpError && (error.status === 401 || error.status === 403);
}

function clearRefreshRetry() {
    if (refreshRetryTimer) {
        clearTimeout(refreshRetryTimer);
        refreshRetryTimer = undefined;
    }
}

function scheduleRefreshRetry() {
    clearRefreshRetry();
    const capped = Math.min(REFRESH_RETRY_BASE_MS * 2 ** refreshFailures, REFRESH_RETRY_MAX_MS);
    // Jitter so tabs that all dropped during the same outage don't retry in lockstep.
    const delay = capped / 2 + Math.random() * (capped / 2);
    refreshFailures += 1;
    refreshRetryTimer = setTimeout(() => {
        void Api.refresh();
    }, delay);
}

/**
 * ROBO-478. Ends the session because the server refused it, then tells the React layer why.
 *
 * Same effect as SessionProvider's `logout` — the persisted session key (localStorage + the legacy
 * `sid<n>` cookie) goes too, not just the in-memory tokens. Done HERE rather than left to the
 * listener so Api stays self-sufficient: with the key left behind, the next `ensureFreshToken` would
 * re-authorize with it and be refused again, once per pending request.
 *
 * Deliberately terminal: no retry timer, no refresh timer, no navigation. The notice is the UI's job.
 */
function refuseSession(reason: SessionRefusalReason) {
    clearRefreshRetry();
    refreshFailures = 0;
    if (Api.refreshTimer) {
        clearTimeout(Api.refreshTimer);
        Api.refreshTimer = undefined;
    }
    Api.accessToken = undefined;
    Api.refreshToken = undefined;
    Api.validTo = undefined;
    Api.user = undefined;
    Auth.clearPersistedSessionKey();
    Api.onSessionRefused?.(reason);
}

const Api: ApiInterface = {
    apiBypass: [],

    get apiHost() {
        return Http.apiHost;
    },
    set apiHost(value: string) {
        Http.setApiHost(value);
    },

    apiLog: [],

    authorize: async (sessionKey?: string): Promise<string | undefined> => {
        if (Api.accessToken) {
            return Api.accessToken;
        }
        if (!authorizePromise) {
            authorizePromise = (async () => {
                try {
                    await ensureHostInfo();
                    const response = await Auth.authorize(sessionKey);
                    Api.sessionUpdate(response);
                    if (Api?.user?.isAdmin) {
                        await Api.bypassLoad();
                    }
                    // The authorize round trip just renewed the legacy session — it reaches
                    // tblSession through proc_APIGetSession — so record it as the renewal it is.
                    // persistSession() then schedules against the shared clock instead of firing
                    // a redundant ping on top of it, which is what every full document load used
                    // to do.
                    stampKeepAlive();
                    Api.persistSession();
                    return Api.accessToken;
                } catch (error) {
                    console.error(error);
                    // ROBO-478: a refused boot authorize (a legacy session cookie, `?SessionKey=`,
                    // or the Google return) resolves undefined like any failure, but also ends the
                    // session and reports why, so the login screen can say so.
                    if (isBrandSuspendedError(error)) {
                        refuseSession('brand-suspended');
                    }
                    return undefined;
                } finally {
                    authorizePromise = null;
                }
            })();
        }
        return authorizePromise;
    },

    bypassFind: (verb: string, url: string): ApiResponse | undefined => {
        return Bypass.bypassFind(Api.apiBypass, verb, url);
    },

    bypassLoad: async () => {
        try {
            const bypasses = await Bypass.bypassLoad(Api.user?.sessionKey);
            Api.apiBypass = bypasses;
            Api?.bypassSet?.(Api.apiBypass);
        } catch (e) {
            console.error(e);
        }
    },

    bypassSave: async (bypasses: ApiBypass[]) => {
        try {
            await Bypass.bypassSave(bypasses, Api.user?.sessionKey);
        } catch (error) {
            console.error(error);
        }
    },

    databaseLog: [],

    /**
     * The access token to send with a request, refreshed first if it is expired or about
     * to be. Callers that hold a token across several requests — the AI chat's tool loop
     * runs up to 20 rounds off one user message — must call this per request rather than
     * reusing a value captured when the turn started.
     *
     * Refresh is single-flight, so concurrent callers share one round trip. A transient
     * failure leaves the existing token in place and returns it: it may already be dead,
     * but the retry is scheduled and failing the request outright helps no one.
     */
    ensureFreshToken: async (): Promise<string | undefined> => {
        if (!Api.accessToken) {
            return Api.authorize();
        }
        if (Api.validTo && Api.validTo.getTime() - Date.now() <= TOKEN_STALE_THRESHOLD_MS) {
            await Api.refresh();
        }
        return Api.accessToken;
    },

    delete: async (url: string, skipAuth?: Boolean) => {
        return Api.fetch('DELETE', url, undefined, skipAuth);
    },

    fetch: async <T, U = unknown>(
        method: RequestInit['method'],
        url: string,
        payload?: T,
        skipAuth?: Boolean,
        opts?: ApiRequestOptions
    ) => {
        // setup event
        const event = Api.logApiRequest(method, url, payload);

        // check for bypass
        let response: ApiResponse<U>;
        const bypassResponse = Api.bypassFind(method, url);
        if (bypassResponse) {
            console.log(`API Bypass used for: ${url}.`, { variant: 'warning' });
            response = bypassResponse as ApiResponse<U>;
        } else {
            const headers: HeadersInit = {};
            if (!skipAuth) {
                const auth = await Api.ensureFreshToken();

                if (!auth) {
                    console.error('could not authorize api');
                    return {
                        error: new Error('could not authorize api'),
                        ok: false,
                        status: 100,
                        statusText: 'could not authorize api',
                        url,
                        redirected: false
                    };
                }
                headers.Authorization = `Bearer ${auth}`;
            }

            try {
                // The caller's headers first, so the bearer token set above cannot be replaced.
                response = await Http.request<U, T>(method, url, payload, {
                    ...opts,
                    headers: { ...headersToRecord(opts?.headers), ...headers }
                });
            } catch (error) {
                response = {
                    error: error as Error,
                    ok: false,
                    status: 100,
                    statusText: String(error),
                    url,
                    redirected: false
                };
            }
        }

        // return completed response
        return Api.response<U>(response, event);
    },

    get: async <T = unknown, U = unknown>(
        url: string,
        data?: T,
        skipAuth?: Boolean,
        opts?: ApiRequestOptions
    ) => {
        const query = data ? new URLSearchParams(data).toString() : '';
        return Api.fetch<T, U>('GET', query ? `${url}?${query}` : url, undefined, skipAuth, opts);
    },

    hasTouchScreen,

    hostInfo: {},

    isMobile,

    login: async (
        email: string,
        password: string,
        rememberMe: boolean = true
    ): Promise<string | undefined> => {
        if (Api.accessToken) {
            return Api.accessToken;
        }

        try {
            // The host's brand scopes the login (see Auth.login) — GroupAdmin/Client/Assistant
            // are rejected outright when it is wrong.
            const hostInfo = await ensureHostInfo();
            const response = await Auth.login(email, password, hostInfo?.brandId);
            Api.sessionUpdate(response, rememberMe);
            return Api.accessToken;
        } catch (error) {
            // ROBO-478: still undefined (the caller's "not signed in"), but a suspended business's
            // refusal is reported so it is not shown as a wrong password.
            if (isBrandSuspendedError(error)) {
                refuseSession('brand-suspended');
            }
            return undefined;
        }
    },

    logApiRequest: <T = unknown>(method: string, url: string, payload: T) => {
        const event = Logger.logApiRequest({
            method,
            url,
            payload,
            logs: Api.apiLog,
            notify: Api.apiNotify
        });

        return event;
    },

    patch: async <T, U>(url: string, data?: T, skipAuth?: Boolean, opts?: ApiRequestOptions) => {
        return Api.fetch<T, U>('PATCH', url, data, skipAuth, opts);
    },

    post: async <T, U>(url: string, data?: T, skipAuth?: Boolean, opts?: ApiRequestOptions) => {
        return Api.fetch<T, U>('POST', url, data, skipAuth, opts);
    },

    put: async <T, U>(url: string, data?: T, skipAuth?: Boolean) => {
        return Api.fetch<T, U>('PUT', url, data, skipAuth);
    },

    /**
     * Keeps the legacy ASP session alive, on a clock shared with the legacy app through
     * localStorage — see `services/session-keepalive`.
     *
     * This used to fire unconditionally: once on every full document load (authorize calls it)
     * and then every 10 minutes per tab, with no knowledge of what any other tab or the legacy
     * app had already done. Now it pings only when nothing has renewed the session for a whole
     * interval, and re-arms against the shared timestamp rather than a private one.
     */
    persistSession: async () => {
        let delay = KEEPALIVE_RETRY_MS;
        try {
            // The sessionKey guard matters: without it a logged-out tab would fall through to
            // stampKeepAlive() and suppress a live tab's ping without having renewed anything.
            if (keepAliveDue() && Api.user?.sessionKey) {
                await Bypass.persistSession(Api.user.sessionKey);
                stampKeepAlive();
            }
            delay = keepAliveDelay();
        } catch (error) {
            console.error(error);
            // Nothing was renewed, so do not stamp. Retry well inside the 20 minute server-side
            // idle window instead of re-arming for a full interval.
            delay = KEEPALIVE_RETRY_MS;
        } finally {
            clearTimeout(Api.persistTmr);
            Api.persistTmr = setTimeout(Api.persistSession, delay);
        }
    },

    refresh: async (): Promise<Boolean> => {
        // Single-flight. The scheduled timer, the visibilitychange handler and a pending
        // retry can all fire at once; letting them run concurrently would race to rotate
        // the refresh token, and the losing call would come back 401 and look terminal.
        if (refreshInFlight) {
            return refreshInFlight;
        }

        const run = (async (): Promise<boolean> => {
            try {
                const response = await Auth.refresh({
                    accessToken: Api.accessToken,
                    refreshToken: Api.refreshToken
                });
                clearRefreshRetry();
                refreshFailures = 0;
                Api.sessionUpdate(response);
                return true;
            } catch (error) {
                console.error(error);

                // ROBO-478: the business was suspended mid-session. Terminal like any rejection
                // below, but it also ends the session outright and reports why. Checked FIRST: a
                // bare 403 (invalid or expired refresh token) must still take the path below.
                if (isBrandSuspendedError(error)) {
                    refuseSession('brand-suspended');
                    return false;
                }

                if (isTerminalRefreshError(error)) {
                    clearRefreshRetry();
                    refreshFailures = 0;
                    if (Api.refreshTimer) {
                        clearTimeout(Api.refreshTimer);
                        Api.refreshTimer = undefined;
                    }
                    Api.accessToken = undefined;
                    Api.refreshToken = undefined;
                    Api.validTo = undefined;
                    return false;
                }

                // Transient failure: leave the session untouched. Clearing validTo here is
                // what used to strand the session permanently — the refresh timer is
                // one-shot and already spent, and the visibilitychange handler bails when
                // validTo is missing, so nothing was left to retry.
                //
                // Retrying at the capped interval without an attempt limit is deliberate:
                // the server, not a local guess, decides when the session is over. Once the
                // refresh token really does expire, the next attempt returns 401 and takes
                // the terminal path above.
                scheduleRefreshRetry();
                return false;
            } finally {
                // Safe to clear unconditionally: while a refresh is in flight every other
                // caller returns this same promise, so no newer run can have replaced it.
                refreshInFlight = null;
            }
        })();

        refreshInFlight = run;
        return run;
    },

    requestLog: [],

    response: async <U = unknown>(response: ApiResponse<U>, event: Logger.LogEntry) => {
        if (event) {
            Api.apiLog = Logger.updateApiLogEntry({
                event,
                response,
                data: response.data,
                logs: Api.apiLog,
                notify: Api.apiNotify
            });
        }

        const data = response.data as ApiResponseData<U>;

        const path = response.url;
        Api.databaseLog = Logger.logOperations({
            path,
            operations: data?.database,
            logs: Api.databaseLog,
            notify: Api.databaseNotify
        });

        Api.requestLog = Logger.logOperations({
            path,
            operations: data?.requests ?? [],
            logs: Api.requestLog,
            notify: Api.requestNotify
        });

        Api.webhookLog = Logger.logOperations({
            path,
            operations: data?.webhooks ?? [],
            logs: Api.webhookLog,
            notify: Api.webhookNotify
        });

        return {
            ...(response.data ?? {}),
            headers: response.headers,
            ok: response.ok,
            redirected: response.redirected,
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            error: response?.error
        };
    },

    sessionUpdate: (data: AuthResponse, persist: boolean = true) => {
        Api.accessToken = data.accessToken;
        Api.refreshToken = data.refreshToken;
        Api.validTo = new Date(data.validTo);
        Api.user = buildUserFromAuthResponse(data);
        if (persist) {
            localStorage.setItem('sessionKey', data.sessionKey);
        }

        const diff = Api.validTo.getTime() - Date.now() - 10000;
        if (Api.refreshTimer) {
            clearTimeout(Api.refreshTimer);
        }
        Api.refreshTimer = setTimeout(Api.refresh, diff);
        Api.onSessionUpdate?.(data, persist);
    },

    user: undefined,

    webhookLog: []
};

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        if (!Api.validTo) return;
        const msRemaining = Api.validTo.getTime() - Date.now();
        if (msRemaining < TOKEN_STALE_THRESHOLD_MS) {
            Api.refresh();
        }
    });
}

if (typeof window !== 'undefined') {
    // Recover the moment connectivity returns instead of waiting out the backoff, which
    // can be sitting on a 30s delay by the time a longer outage ends. Harmless when the
    // token is still good: refresh is single-flight and this only fires on a real
    // offline-to-online transition.
    window.addEventListener('online', () => {
        if (!Api.validTo) return;
        Api.refresh();
    });

    // Another tab renewed the session — a React boot, a ping, or a legacy page load, which stamps
    // the same key from hd.persist_* in the legacy js/main.js. Re-arm against the shared clock so
    // this tab defers instead of pinging on its own now-stale schedule.
    //
    // Guarded on an existing timer: a tab that never authorized has no keep-alive to re-arm, and
    // starting one here would ping for a session it does not have.
    window.addEventListener('storage', event => {
        if (event.key !== KEEPALIVE_KEY) return;
        if (!Api.persistTmr) return;
        clearTimeout(Api.persistTmr);
        Api.persistTmr = setTimeout(Api.persistSession, keepAliveDelay());
    });
}

export default Api;
